// Sessions — every orchestration thread in the org, newest first.
import { useClient } from '@cortex/react-query';
import type { AgentListItem, PersistentAgentListItem, PersistentAgentStatus } from '@cortex/types';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useMemo, useState } from 'react';

import { PageHeader } from '../../components/common/PageHeader';
import { Spinner } from '../../components/common/Spinner';
import { routes } from '../../lib/constants';
import { formatTokens, formatUsd } from '../../lib/format';
import { tdStyle, thStyle } from '../../lib/table-styles';

export const Route = createFileRoute('/agents/sessions')({ component: SessionsPage });

interface IListEnvelope<T> {
  items: T[];
  total: number;
}

const pageSize = 25;

const statusBadge: Record<string, string> = {
  STARTING: 'badge badge--warning',
  IDLE: 'badge badge--neutral',
  WORKING: 'badge badge--success',
  PAUSED: 'badge badge--warning',
  WAITING_FOR_INPUT: 'badge badge--warning',
  STOPPED: 'badge badge--default',
  FAILED: 'badge badge--danger',
};

const statusLabel: Record<string, string> = {
  STARTING: 'Starting',
  IDLE: 'Idle',
  WORKING: 'Working',
  PAUSED: 'Paused',
  WAITING_FOR_INPUT: 'Waiting',
  STOPPED: 'Stopped',
  FAILED: 'Failed',
};

const activeStatuses = new Set<PersistentAgentStatus>([
  'STARTING',
  'IDLE',
  'WORKING',
  'PAUSED',
  'WAITING_FOR_INPUT',
]);

type CreatedWindow = 'all' | '24h' | '7d' | '30d';

function SessionsPage() {
  const client = useClient();
  const nav = useNavigate();
  const [query, setQuery] = useState('');
  const [created, setCreated] = useState<CreatedWindow>('all');
  const [agentId, setAgentId] = useState('all');
  const [status, setStatus] = useState<'all' | 'active' | PersistentAgentStatus>('active');
  const [page, setPage] = useState(0);

  const agentsQuery = useQuery({
    queryKey: ['agents'],
    queryFn: () => client.http.get('v1/agents').json<IListEnvelope<AgentListItem>>(),
  });
  const sessionsQuery = useQuery({
    queryKey: ['persistent-agents', 'sessions', page],
    queryFn: () =>
      client.http
        .get(`v1/persistent-agent-groups/agents?limit=${pageSize}&offset=${page * pageSize}`)
        .json<IListEnvelope<PersistentAgentListItem>>(),
  });

  const agentNameById = useMemo(() => {
    const names = new Map<string, string>();
    for (const agent of agentsQuery.data?.items ?? []) names.set(agent.id, agent.name);
    return names;
  }, [agentsQuery.data]);

  const rows = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const since = createdSince(created);
    return (sessionsQuery.data?.items ?? []).filter((thread) => {
      if (agentId !== 'all' && thread.agentId !== agentId) return false;
      if (status === 'active' && !activeStatuses.has(thread.status)) return false;
      if (status !== 'all' && status !== 'active' && thread.status !== status) return false;
      if (since && new Date(thread.startedAt).getTime() < since) return false;
      if (!needle) return true;
      const name = (thread.name ?? '').toLowerCase();
      return thread.id.toLowerCase().includes(needle) || name.includes(needle);
    });
  }, [sessionsQuery.data, query, created, agentId, status]);

  const total = sessionsQuery.data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));

  const openThread = (thread: PersistentAgentListItem) => {
    nav({ to: routes.orchestrationThread(thread.agentId, thread.id) }).catch((err) =>
      console.error(err),
    );
  };

  return (
    <>
      <PageHeader title="Sessions" />
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 'var(--sp-3)',
          marginBottom: 'var(--sp-3)',
          alignItems: 'end',
        }}
      >
        <label className="field" style={{ minWidth: 240, flex: '1 1 240px' }}>
          <span className="field-label">Find session by ID</span>
          <input
            className="input"
            value={query}
            placeholder="id"
            onChange={(event) => {
              setQuery(event.target.value);
              setPage(0);
            }}
          />
        </label>
        <label className="field">
          <span className="field-label">Created</span>
          <select
            className="input"
            value={created}
            onChange={(event) => {
              setCreated(event.target.value as CreatedWindow);
              setPage(0);
            }}
          >
            <option value="all">All time</option>
            <option value="24h">Last 24 hours</option>
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
          </select>
        </label>
        <label className="field">
          <span className="field-label">Agent</span>
          <select
            className="input"
            value={agentId}
            onChange={(event) => {
              setAgentId(event.target.value);
              setPage(0);
            }}
          >
            <option value="all">All</option>
            {(agentsQuery.data?.items ?? [])
              .filter((agent) => agent.kind === 'ORCHESTRATION')
              .map((agent) => (
                <option key={agent.id} value={agent.id}>
                  {agent.name}
                </option>
              ))}
          </select>
        </label>
        <label className="field">
          <span className="field-label">Status</span>
          <select
            className="input"
            value={status}
            onChange={(event) => {
              setStatus(event.target.value as typeof status);
              setPage(0);
            }}
          >
            <option value="active">Active</option>
            <option value="all">All</option>
            <option value="IDLE">Idle</option>
            <option value="WORKING">Working</option>
            <option value="WAITING_FOR_INPUT">Waiting</option>
            <option value="PAUSED">Paused</option>
            <option value="STARTING">Starting</option>
            <option value="STOPPED">Stopped</option>
            <option value="FAILED">Failed</option>
          </select>
        </label>
      </div>

      {sessionsQuery.isLoading ? (
        <Spinner />
      ) : sessionsQuery.error ? (
        <div className="alert alert--danger">
          Failed to load sessions: {(sessionsQuery.error as Error).message}
        </div>
      ) : rows.length === 0 ? (
        <p className="t-sm t-muted">No sessions match these filters.</p>
      ) : (
        <div className="card" style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 880 }}>
            <thead>
              <tr>
                <th style={thStyle}>ID</th>
                <th style={thStyle}>Name</th>
                <th style={thStyle}>Status</th>
                <th style={thStyle}>Agent</th>
                <th style={thStyle}>Cost</th>
                <th style={thStyle}>Tokens in / out</th>
                <th style={thStyle}>Started</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((thread) => (
                <tr
                  key={thread.id}
                  onClick={() => openThread(thread)}
                  style={{ cursor: 'pointer', borderTop: '1px solid var(--border-subtle)' }}
                  onMouseEnter={(event) => {
                    for (const cell of event.currentTarget.children) {
                      (cell as HTMLElement).style.background = 'rgba(44,65,66,0.08)';
                    }
                  }}
                  onMouseLeave={(event) => {
                    for (const cell of event.currentTarget.children) {
                      (cell as HTMLElement).style.background = '';
                    }
                  }}
                >
                  <td style={{ ...tdStyle, fontFamily: 'var(--font-mono)', fontSize: 12 }}>
                    {thread.id}
                  </td>
                  <td style={{ ...tdStyle, fontWeight: 500, color: 'var(--text-primary)' }}>
                    {thread.name?.trim() || 'Untitled'}
                  </td>
                  <td style={tdStyle}>
                    <span className={statusBadge[thread.status] ?? 'badge'}>
                      {statusLabel[thread.status] ?? thread.status}
                    </span>
                  </td>
                  <td style={tdStyle}>
                    <Link
                      to="/agents/$agentId"
                      params={{ agentId: thread.agentId }}
                      onClick={(event) => event.stopPropagation()}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                        maxWidth: '100%',
                        height: 24,
                        padding: '0 8px 0 4px',
                        borderRadius: 'var(--radius-sm)',
                        background: 'var(--surface-raised)',
                        border: '1px solid var(--border)',
                        color: 'var(--text-secondary)',
                        fontSize: 12,
                        lineHeight: 1,
                        textDecoration: 'none',
                      }}
                    >
                      <RobotFaceIcon />
                      <span
                        style={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {agentNameById.get(thread.agentId) ?? thread.agentId}
                      </span>
                    </Link>
                  </td>
                  <td style={{ ...tdStyle, color: 'var(--text-secondary)' }}>
                    {formatUsd(thread.totalCostUsd)}
                  </td>
                  <td
                    style={{
                      ...tdStyle,
                      color: 'var(--text-secondary)',
                      fontFamily: 'var(--font-mono)',
                      fontSize: 12,
                    }}
                  >
                    {formatTokens(thread.tokensIn ?? 0)} / {formatTokens(thread.tokensOut ?? 0)}
                  </td>
                  <td style={{ ...tdStyle, color: 'var(--text-secondary)' }}>
                    {formatStarted(thread.startedAt)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginTop: 'var(--sp-3)',
        }}
      >
        <span className="t-sm t-muted">
          {total === 0
            ? '0 sessions'
            : `${page * pageSize + 1}–${Math.min(total, (page + 1) * pageSize)} of ${total}`}
        </span>
        <div style={{ display: 'flex', gap: 'var(--sp-2)' }}>
          <button
            className="btn btn--ghost btn--sm"
            disabled={page === 0}
            onClick={() => setPage((current) => Math.max(0, current - 1))}
          >
            Previous
          </button>
          <button
            className="btn btn--ghost btn--sm"
            disabled={page + 1 >= pageCount}
            onClick={() => setPage((current) => current + 1)}
          >
            Next
          </button>
        </div>
      </div>
    </>
  );
}

function RobotFaceIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      style={{ flex: 'none', color: 'var(--text-muted)' }}
    >
      <rect x="4" y="7" width="16" height="12" rx="2" />
      <path d="M9 12h.01M15 12h.01" />
      <path d="M12 3v4" />
    </svg>
  );
}

function createdSince(window: CreatedWindow): number | null {
  const day = 24 * 60 * 60 * 1000;
  if (window === '24h') return Date.now() - day;
  if (window === '7d') return Date.now() - 7 * day;
  if (window === '30d') return Date.now() - 30 * day;
  return null;
}

const startedFmt = new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric' });

function formatStarted(value: Date | string): string {
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : startedFmt.format(date);
}
